import React, { useEffect, useMemo, useState } from "react";
import CommentExplorer from "../comments/explorer/CommentExplorer";
import type { CommentExplorerMode } from "../comments/explorer/types";
import "../comments/explorer/CommentExplorer.css";
import { buildDiscussionModel } from "./model/buildDiscussionModel";
import {
  getAnchorForSelectedComment,
  getSafeSelectedCommentId,
} from "./model/discussionSelection";
import type {
  CanonicalCommentWorkspaceMode,
  CanonicalCommentWorkspaceProps,
} from "./model/types";
import { BranchReader } from "./components/BranchReader";
import { WorkspaceShell } from "./components/WorkspaceShell";
import { WorkspaceTopBar } from "./components/WorkspaceTopBar";
import { WorkspaceComposer } from "./components/WorkspaceComposer";
import styles from "./CanonicalCommentWorkspace.module.css";

function toExplorerMode(mode: CanonicalCommentWorkspaceMode): CommentExplorerMode {
  if (mode === "branch") return "reader";
  return mode;
}

export const CanonicalCommentWorkspace: React.FC<CanonicalCommentWorkspaceProps> = ({
  adapter,
  surface = "docked",
  initialMode = "branch",
  selectedCommentId,
  selectedAnchorId,
  onSelectedCommentChange,
  onSelectedAnchorChange,
  onRequestExpand,
  onRequestDock,
  onRequestClose,
  className = "",
}) => {
  const [mode, setMode] = useState<CanonicalCommentWorkspaceMode>(initialMode);
  const [internalSelectedCommentId, setInternalSelectedCommentId] = useState<number | null>(null);

  const model = useMemo(
    () => buildDiscussionModel(adapter.comments, adapter.anchors || []),
    [adapter.anchors, adapter.comments],
  );

  const requestedSelectedCommentId =
    typeof selectedCommentId === "number" ? selectedCommentId : internalSelectedCommentId;
  const safeSelectedCommentId = getSafeSelectedCommentId(
    model.byId,
    model.roots,
    requestedSelectedCommentId,
  );

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (typeof selectedAnchorId !== "number") return;
    const anchor = model.anchorsById.get(selectedAnchorId);
    if (!anchor?.rootCommentId) return;

    setInternalSelectedCommentId(anchor.rootCommentId);
    onSelectedCommentChange?.(anchor.rootCommentId);
    setMode("branch");
  }, [model.anchorsById, onSelectedCommentChange, selectedAnchorId]);

  useEffect(() => {
    if (!model.all.length) {
      if (internalSelectedCommentId !== null) setInternalSelectedCommentId(null);
      return;
    }

    if (safeSelectedCommentId !== internalSelectedCommentId) {
      setInternalSelectedCommentId(safeSelectedCommentId);
      onSelectedCommentChange?.(safeSelectedCommentId);
    }
  }, [internalSelectedCommentId, model.all.length, onSelectedCommentChange, safeSelectedCommentId]);

  useEffect(() => {
    if (typeof selectedAnchorId === "number" && !model.anchorsById.has(selectedAnchorId)) {
      return;
    }

    if (!model.all.length) return;

    const selectedComment = safeSelectedCommentId === null ? null : model.byId.get(safeSelectedCommentId) ?? null;
    const anchor = getAnchorForSelectedComment(
      model.byId,
      model.anchorsByRootCommentId,
      selectedComment,
    );
    const nextAnchorId = anchor?.id ?? null;

    if ((selectedAnchorId ?? null) !== nextAnchorId) {
      onSelectedAnchorChange?.(nextAnchorId);
    }
  }, [
    model.all.length,
    model.anchorsById,
    model.anchorsByRootCommentId,
    model.byId,
    onSelectedAnchorChange,
    safeSelectedCommentId,
    selectedAnchorId,
  ]);

  const handleSelectComment = (commentId: number | null) => {
    setInternalSelectedCommentId(commentId);
    onSelectedCommentChange?.(commentId);

    if (commentId === null) {
      onSelectedAnchorChange?.(null);
      return;
    }

    const selectedComment = model.byId.get(commentId) ?? null;
    const anchor = getAnchorForSelectedComment(
      model.byId,
      model.anchorsByRootCommentId,
      selectedComment,
    );
    onSelectedAnchorChange?.(anchor?.id ?? null);
  };

  const currentEditId = safeSelectedCommentId
    ? model.byId.get(safeSelectedCommentId)?.editId ?? null
    : null;

  const rootComposer = (
    <WorkspaceComposer
      canComment={adapter.canComment}
      canCreateAnchoredComment={adapter.canCreateAnchoredComment}
      hasComments={model.all.length > 0}
      targetLabel={adapter.target.title || "this Gist"}
      onCreateRootComment={adapter.createRootComment}
    />
  );

  return (
    <div className={`${styles.workspaceHost} ${className}`.trim()}>
      <WorkspaceShell
        surface={surface}
        title={adapter.target.title || "Discussion"}
        subtitle={adapter.target.subtitle || "Canonical Comment Workspace"}
        loading={adapter.loading}
        error={adapter.error}
        onRequestExpand={onRequestExpand}
        onRequestDock={onRequestDock}
        onRequestClose={onRequestClose}
      >
        <WorkspaceTopBar mode={mode} model={model} surface={surface} onModeChange={setMode} />

        {rootComposer}

        <div className={styles.workspaceBody}>
          {mode === "branch" ? (
            <BranchReader
              model={model}
              selectedCommentId={safeSelectedCommentId}
              adapter={adapter}
              onSelect={(commentId) => handleSelectComment(commentId)}
            />
          ) : (
            <CommentExplorer
              gistId={Number(adapter.target.id) || 0}
              editId={currentEditId}
              comments={adapter.comments}
              loading={adapter.loading}
              error={adapter.error}
              currentUserId={adapter.currentUserId}
              currentUserRole={adapter.currentUserRole}
              title="Explore this discussion"
              subtitle="Workspace mode"
              initialMode={toExplorerMode(mode)}
              selectedCommentId={safeSelectedCommentId}
              onSelectedCommentChange={handleSelectComment}
              replyUnavailableMessage="Log in to reply to this comment."
              showOrphanCount
              onReply={adapter.replyToComment}
              onSoftRemove={adapter.softRemoveComment}
              onHardDeleteThread={adapter.hardDeleteThread}
            />
          )}
        </div>
      </WorkspaceShell>
    </div>
  );
};
